import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl} from 'react-intl';
import 'react-phone-number-input/style.css'
import strings from '../../utils/strings'
import API from '../../utils/API';
import commonFunctions from '../../utils/commonFunctions';

function RoleModal(props) {
  const { 
    setResetRoleModalRef,
    roles,
    dataMap,
    loadTable,
    setSuccess,
    closeModal,
  } = props;

  //Implemented multi-language support
  const intl = useIntl();
  const translation = (id) => intl.formatMessage({ id });

  // Role modal field states
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [modalError, setModalError] = useState('');
  const [selectAll, setSelectAll] = useState(false); // State to manage "Select All" checkbox

  useEffect(() => {
    setResetRoleModalRef(resetModal);
  }, [setResetRoleModalRef]);

  // Initialize modal when it's shown
  useEffect(() => {
    const modal = document.getElementById('setRolesModal');
    
    if (modal) {
      const handleModalShow = () => {
        // Reset state when modal is shown
        resetModal();
        
        // Get all selected user IDs
        const selectedUserIds = Object.keys(dataMap.selectedIds)
          .filter(id => dataMap.selectedIds[id] === 1);
        
        if (selectedUserIds.length > 0) {
          console.log('Selected users:', selectedUserIds.length);
          
          // Get the first user's roles as the baseline
          const firstUserId = selectedUserIds[0];
          const firstUser = dataMap.data[firstUserId];
          
          if (firstUser && firstUser.roles && firstUser.roles.length > 0) {
            // Get role IDs from first user's roles
            const firstUserRoleIds = firstUser.roles.map(role => role.id);
            console.log('First user role IDs:', firstUserRoleIds);
            
            // Check if all selected users have the same roles
            let allUsersHaveSameRoles = true;
            
            // Skip the first user since we already processed it
            for (let i = 1; i < selectedUserIds.length; i++) {
              const userId = selectedUserIds[i];
              const user = dataMap.data[userId];
              
              if (!user || !user.roles) {
                allUsersHaveSameRoles = false;
                break;
              }
              
              // Get current user's role IDs
              const userRoleIds = user.roles.map(role => role.id);
              
              // Check if the roles match the first user's roles
              if (userRoleIds.length !== firstUserRoleIds.length || 
                  !userRoleIds.every(roleId => firstUserRoleIds.includes(roleId))) {
                allUsersHaveSameRoles = false;
                break;
              }
            }
            
            // If all users have the same roles, pre-select them
            if (allUsersHaveSameRoles) {
              console.log('All selected users have the same roles, pre-selecting:', firstUserRoleIds);
              setSelectedRoles(firstUserRoleIds);
              
              // Check if all roles are selected to set selectAll state
              if (firstUserRoleIds.length === roles.length) {
                setSelectAll(true);
              }
            } else {
              console.log('Selected users have different roles, not pre-selecting any');
            }
          }
        }
      };
      
      // Add event listener for modal show
      modal.addEventListener('show.bs.modal', handleModalShow);
      
      // Clean up event listener
      return () => {
        modal.removeEventListener('show.bs.modal', handleModalShow);
      };
    }
  }, [dataMap, roles]);

  const resetModal = () => {
    setSelectedRoles([]);
    setModalError('');
    setSelectAll(false);
  };

  /**
   * Adds or removes targeted value from selectedRoles
   * @param {Event} e
   */
  function setSelectedRole(e) {
    let newArray = [...selectedRoles];
    if (newArray.includes(e.target.value)) {
      let index = newArray.indexOf(e.target.value);
      if (index !== -1) {
        newArray.splice(index, 1);
      }
    } else {
      newArray.push(e.target.value);
    }
    setSelectedRoles(newArray);
  }

  /**
   * Handles "Select All" checkbox change
   * @param {Event} e
   */
  function handleSelectAll(e) {
    setSelectAll(e.target.checked);
    if (e.target.checked) {
      setSelectedRoles(roles.map(role => role.id));
    } else {
      setSelectedRoles([]);
    }
  }

  /**
   * Assign roles button handler
   * @param {Event} e DOM Event
   */
  const handleAssignRoles = async (e) => {
    const selectedUserIds = Object.keys(dataMap.selectedIds)
      .filter(id => dataMap.selectedIds[id] === 1); // Filter selected user IDs

    if (selectedUserIds.length === 0) {
      setModalError('NO_USERS_SELECTED');
      return;
    }

    //Bug #19054 Cannot De-assign Roles or Sites from users.
    /*if (selectedRoles.length === 0) {
      setModalError(<FormattedMessage id="NO_ROLES_SELECTED" />);
      return;
    }*/

    // Filter out any null values from selectedRoles
    //Mei 2025-5-14 fixed bug#19565 filter out the Account Owner
    const filteredRoles = selectedRoles.filter(role => (role !== null) && (role !== "c3c29b52-41d6-45ec-8064-2c558d1a5b2f"));
    // console.log('filteredRoles', filteredRoles);
    
    try {
      await API.setRoles(selectedUserIds, filteredRoles, "");
      closeModal("setRolesModal");
      resetModal();
      await loadTable(dataMap);
      setSuccess(<FormattedMessage id="EDIT_USER_ROLES_SUCCESS" />);
    } catch (err) {
      console.error(`Failed to edit user-roles: ${err}`);
      setModalError(<FormattedMessage id="EDIT_USER_ROLES_FAILED" values={{errorMsg: <b>{err.message}</b>,}} />);
    }

    e.preventDefault();
  };

  return (
    <div className="modal fade" id="setRolesModal" tabIndex="-1" aria-labelledby="roleModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title"><FormattedMessage id="ASSIGN_ROLES" /></h5>
            <button id="closeRolesModal" type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <div className="card" style={{'borderRadius':'8px'}}>
              <div style={{overflowX: 'hidden'}}>
                <table className="table">
                  <thead>
                    <tr>
                      <th className="customHeaderCell" scope="col">
                        <input 
                          id="selectAll"
                          type="checkbox" 
                          className="form-check-input" 
                          checked={selectAll} 
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="customHeaderCell" scope="col"><FormattedMessage id="FEATURE" /></th>
                      <th className="customHeaderCell" scope="col"><FormattedMessage id="ROLE" /></th>
                      <th className="customHeaderCell" scope="col"><FormattedMessage id="ROLE_DESCRIPTION" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {/*roles*/commonFunctions.getRolesSort(roles).map((role) => {
                      return (
                        <tr key={role.id}>
                          <th className="customBodyCell" scope="row">
                            <input 
                              id="roleCheckbox"
                              className="form-check-input" 
                              type="checkbox" 
                              value={role.id}
                              checked={selectedRoles.includes(role.id)}
                              onChange={(e) => { setSelectedRole(e); }}
                            />
                          </th>
                          <td className="customBodyCell" >
                            {
                              (role.features.length > 0) ? 
                              role.features.map((feature) => { return translation(`${feature.id}-FEATURENAME`); }).join(', ') : 
                              translation('672597bc-8086-4457-b036-690c985ef829-FEATURENAME')
                            }
                          </td>
                          <td className="customBodyCell" ><FormattedMessage id={`${role.id}-ROLENAME`}/></td>
                          <td className="customBodyCell roleDescription" ><FormattedMessage id={`${role.id}-DESCRIPTION`}/></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            {modalError && (
              <div className="modal-error invalid-feedback d-block fw-bold text-center">
                {modalError}
              </div>
            )}
          </div>
          <div className="modal-footer justify-content-between">
            <button id="backRoleModalBtn" className="btn back-btn" type="button" data-bs-dismiss="modal"><FormattedMessage id="BACK" /></button>
            <button id="saveRolesBtn" className="btn save-btn ms-2" type="button" onClick={handleAssignRoles}><FormattedMessage id="SAVE" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleModal;